import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trackEvent } from '../services/analytics';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const PAGE = {
  url: 'https://stellarreply.com/airbnb-superhost-checker',
  title: 'Airbnb Superhost Checker - Are You Above 4.8? | StellarReply',
  metaDescription: 'Paste your Airbnb listing URL to see exactly where you stand against the 4.8 Superhost threshold: how many 5-star reviews you need, what one bad review would cost you, and how long until the next assessment. Free, no signup.',
  ogDescription: 'See exactly where your Airbnb listing stands against the 4.8 Superhost threshold, and precisely what it would take to cross it.',
  schemaName: 'Airbnb Superhost Checker',
};

// The scale runs across the band Airbnb listings actually occupy. Anything
// below 4.0 pins to the left rather than compressing the useful range.
const SCALE_MIN = 4.0;
const SCALE_MAX = 5.0;
const pos = (v) => Math.max(0, Math.min(100, ((v - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100));

function useMetaTags() {
  useEffect(() => {
    document.title = PAGE.title;
    const setMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(name.startsWith('og:') ? 'property' : 'name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', PAGE.metaDescription);
    setMeta('og:title', PAGE.title);
    setMeta('og:description', PAGE.ogDescription);
    setMeta('og:type', 'website');
    setMeta('og:url', PAGE.url);

    // Self-canonical — the SPA shell ships the homepage canonical, which tells
    // Google this page is a duplicate of /
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute('href') : null;
    if (canonical) canonical.setAttribute('href', PAGE.url);

    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.id = 'standing-schema';
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": PAGE.schemaName,
      "url": PAGE.url,
      "applicationCategory": "BusinessApplication",
      "operatingSystem": "Web",
      "description": PAGE.ogDescription,
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "GBP" }
    });
    document.head.appendChild(schema);
    return () => {
      if (canonical && prevCanonical) canonical.setAttribute('href', prevCanonical);
      document.getElementById('standing-schema')?.remove();
    };
  }, []);
}

/* The signature visual: a measuring rule with your mean marked against the
   two thresholds. The whole product is "a line you are above or below". */
function ThresholdScale({ mean, thresholds }) {
  // Colour tracks Superhost, the threshold that actually matters, not both.
  // Sitting above the line with no buffer left is a warning, not a pass.
  const sh = thresholds.find(t => t.key === 'superhost');
  const safe = sh?.meets && sh.buffer_one_stars > 0;
  const markerColour = safe ? '#16a34a' : '#f59e0b';
  const meanPct = pos(mean);

  return (
    <div style={{ position: 'relative', height: 118, margin: '30px 0 10px' }}>
      {/* track */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: 54, height: 4, background: '#f0ede8', borderRadius: 2 }} />
      <div style={{ position: 'absolute', left: 0, top: 54, height: 4, width: `${meanPct}%`, background: markerColour, borderRadius: 2, transition: 'width .7s ease' }} />

      {/* ticks */}
      {[4.0, 4.25, 4.5, 4.75, 5.0].map(v => (
        <div key={v}>
          <div style={{ position: 'absolute', left: `${pos(v)}%`, top: 54, width: 1, height: 9, background: '#d4d1cc' }} />
          <div style={{ position: 'absolute', left: `${pos(v)}%`, top: 66, transform: 'translateX(-50%)', fontSize: 10, color: '#9ca3af' }}>
            {v.toFixed(2).replace(/0$/, '')}
          </div>
        </div>
      ))}

      {/* Threshold lines. 4.8 and 4.9 are only a tenth apart, so centred labels
          collide — Superhost hangs to the left of its line, Guest Favorite to
          the right of its own. */}
      {thresholds.map((t, i) => {
        const left = i === 0;
        return (
          <div key={t.key}>
            <div style={{
              position: 'absolute', left: `${pos(t.threshold)}%`, top: 36,
              width: 2, height: 24, background: '#1a1a1a', opacity: left ? 1 : 0.35,
            }} />
            <div style={{
              position: 'absolute', left: `${pos(t.threshold)}%`, top: 6,
              transform: left ? 'translateX(-100%)' : 'none',
              paddingRight: left ? 7 : 0, paddingLeft: left ? 0 : 7,
              textAlign: left ? 'right' : 'left',
              whiteSpace: 'nowrap', opacity: left ? 1 : 0.5,
            }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#1a1a1a' }}>{t.threshold.toFixed(1)}</div>
              <div style={{ fontSize: 9, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.06em' }}>{t.label}</div>
            </div>
          </div>
        );
      })}

      {/* you are here */}
      <div style={{
        position: 'absolute', left: `${meanPct}%`, top: 47, width: 18, height: 18,
        borderRadius: '50%', background: markerColour, border: '3px solid #fff',
        transform: 'translateX(-50%)', boxShadow: '0 1px 4px rgba(0,0,0,.18)', transition: 'left .7s ease',
      }} />
      <div style={{
        position: 'absolute', left: `${meanPct}%`, top: 82, transform: 'translateX(-50%)',
        textAlign: 'center', whiteSpace: 'nowrap', transition: 'left .7s ease',
      }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: markerColour, fontVariantNumeric: 'tabular-nums' }}>
          {mean.toFixed(4)}
        </div>
        <div style={{ fontSize: 9, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.06em' }}>You are here</div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone }) {
  const colour = tone === 'warn' ? '#f59e0b' : tone === 'good' ? '#16a34a' : '#1a1a1a';
  return (
    <div style={styles.stat}>
      <div style={styles.statLabel}>{label}</div>
      <div style={{ ...styles.statValue, color: colour }}>{value}</div>
      {sub && <div style={styles.statSub}>{sub}</div>}
    </div>
  );
}

export default function ListingStandingPage() {
  const navigate = useNavigate();
  const { shareId } = useParams();
  const isSharedView = !!shareId;

  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [honeypot, setHoneypot] = useState('');
  const [loadedAt] = useState(() => Date.now());

  // Light gate on the secondary detail only — where you stand is always visible,
  // because that is the number people link to and share.
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [unlocked, setUnlocked] = useState(() => !!localStorage.getItem('sr_free_tool_email'));
  const [unlocking, setUnlocking] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);

  useMetaTags();

  const loadShared = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/free-tools/standing/r/${shareId}`);
      if (!res.ok) throw new Error('This link has expired or does not exist.');
      setResult(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [shareId]);

  useEffect(() => {
    if (isSharedView) loadShared();
  }, [isSharedView, loadShared]);

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!url.includes('/rooms/')) {
      setError("That doesn't look like a listing URL - paste a link like airbnb.co.uk/rooms/12345 (find it under Share on your listing).");
      return;
    }
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await fetch(`${API_URL}/free-tools/standing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ airbnb_url: url.trim(), website: honeypot, loaded_at: loadedAt }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        trackEvent('standing_failed', { status: res.status });
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Check failed. Please try again.');
      }
      const report = await res.json();
      setResult(report);
      const sh = report.thresholds.find(t => t.key === 'superhost');
      trackEvent('standing_checked', { meets_superhost: sh?.meets, mean: report.distribution.mean });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!email.includes('@') || !result) return;
    setUnlocking(true);
    try {
      await fetch(`${API_URL}/free-tools/standing/${result.id}/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), marketing_consent: marketingConsent }),
      });
      localStorage.setItem('sr_free_tool_email', email.trim());
      setUnlocked(true);
      trackEvent('standing_lead_captured');
    } catch {
      // Don't block the user on a lead-capture failure
      setUnlocked(true);
    } finally {
      setUnlocking(false);
    }
  };

  const handleCopyShare = () => {
    navigator.clipboard.writeText(result.share_url).then(() => {
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 2000);
    });
  };

  const startTrial = () => navigate('/login?register=true&category=hospitality');

  const showDetail = unlocked || isSharedView;
  const superhost = result?.thresholds?.find(t => t.key === 'superhost');
  const guestFav = result?.thresholds?.find(t => t.key === 'guest_favorite');
  const dist = result?.distribution;

  return (
    <div style={styles.page}>
      <style>{cssText}</style>

      <nav style={styles.nav}>
        <div style={styles.navInner}>
          <div style={styles.logo} onClick={() => navigate('/')}>
            Stellar<span style={{ color: '#16a34a' }}>Reply</span>
          </div>
          <button style={styles.navCta} onClick={startTrial}>Start free trial</button>
        </div>
      </nav>

      <section style={styles.hero}>
        <div style={styles.heroInner}>
          <div style={styles.badge}>Free tool for Airbnb hosts - no signup required</div>
          <h1 style={styles.h1}>
            Are you above<br />
            <span style={{ color: '#16a34a' }}>4.8?</span>
          </h1>
          <p style={styles.heroSub}>
            Airbnb needs a 4.8 to keep Superhost, and reassesses every quarter. Paste your
            listing URL to see exactly where you stand, how many 5-star reviews would close
            the gap, and what a single bad review would cost you.
          </p>

          {!isSharedView && (
            <form onSubmit={handleCheck} style={styles.form}>
              <input
                style={styles.input}
                type="text"
                value={url}
                onChange={e => setUrl(e.target.value)}
                placeholder="https://www.airbnb.co.uk/rooms/12345678"
                maxLength={500}
                disabled={loading}
              />
              <input
                type="text" value={honeypot} onChange={e => setHoneypot(e.target.value)}
                autoComplete="off" tabIndex={-1}
                style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, width: 0 }}
                aria-hidden="true"
              />
              <button
                style={{
                  ...styles.btn, marginTop: 12,
                  opacity: loading || !url.trim() ? 0.45 : 1,
                  cursor: loading || !url.trim() ? 'not-allowed' : 'pointer',
                }}
                type="submit" disabled={loading || !url.trim()}
              >
                {loading ? 'Working out your position...' : 'Show me where I stand'}
              </button>
              <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 8, textAlign: 'center' }}>
                Works with any public Airbnb listing. Find your link under Share on the listing page.
              </div>
            </form>
          )}

          {isSharedView && loading && (
            <div style={{ color: '#6b7280', fontSize: 14, marginTop: 24 }}>Loading report...</div>
          )}

          {error && <div style={styles.errorBox} className="standing-fade">{error}</div>}

          {result && dist && superhost && (
            <div className="standing-fade" style={{ marginTop: 28, textAlign: 'left' }}>

              {/* Listing + the scale */}
              <div style={styles.reportCard}>
                <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 700, marginBottom: 6 }}>
                  Superhost standing by StellarReply
                </div>
                <div style={{ fontSize: 17, fontWeight: 800, color: '#1a1a1a' }}>{result.listing.title}</div>
                <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>
                  {[result.listing.location, `${dist.total} reviews`].filter(Boolean).join(' · ')}
                </div>

                <ThresholdScale mean={dist.mean} thresholds={result.thresholds} />

                <div style={styles.verdict}>
                  {superhost.meets ? (
                    <>
                      You are <strong>{Math.abs(superhost.margin).toFixed(4)} above</strong> the
                      Superhost line, and can absorb{' '}
                      <strong>{superhost.buffer_one_stars} more 1-star review
                      {superhost.buffer_one_stars === 1 ? '' : 's'}</strong> before dropping below it.
                    </>
                  ) : (
                    <>
                      You are <strong>{Math.abs(superhost.margin).toFixed(4)} below</strong> the
                      Superhost line. It would take{' '}
                      <strong>{superhost.five_stars_needed} consecutive 5-star review
                      {superhost.five_stars_needed === 1 ? '' : 's'}</strong> to cross it, landing
                      on exactly {superhost.mean_after_required?.toFixed(4)}.
                    </>
                  )}
                </div>
              </div>

              {/* Headline numbers */}
              <div style={styles.statGrid}>
                <Stat
                  label="Next assessment"
                  value={`${result.assessment.days_remaining} days`}
                  sub={new Date(result.assessment.next_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' })}
                />
                {superhost.meets ? (
                  <Stat label="1-star reviews you can absorb" value={superhost.buffer_one_stars} tone="good" sub="before you drop below 4.8" />
                ) : (
                  <Stat label="5-star reviews needed" value={superhost.five_stars_needed} tone="warn" sub={`lands on exactly ${superhost.mean_after_required?.toFixed(4)}`} />
                )}
                <Stat
                  label="One 1-star would mean"
                  value={result.fragility.one_star_would_mean.toFixed(4)}
                  tone={result.fragility.one_star_would_mean < 4.8 ? 'warn' : undefined}
                  sub={result.fragility.one_star_would_mean < 4.8 && superhost.meets ? 'that would put you below the line' : null}
                />
                {!superhost.meets && superhost.required_pace_per_month != null && (
                  <Stat
                    label="Pace needed"
                    value={`${superhost.required_pace_per_month}/mo`}
                    tone={superhost.achievable_by_deadline ? undefined : 'warn'}
                    sub={superhost.achievable_by_deadline ? 'to make the deadline' : 'not realistic before this assessment'}
                  />
                )}
              </div>

              {/* Reconstruction — this is the credibility of the page */}
              {dist.verified && (
                <div style={styles.verify}>
                  <div style={{ fontWeight: 700, color: '#166534', marginBottom: 6, fontSize: 13 }}>
                    ✓ Checked against Airbnb's own figure
                  </div>
                  <div style={styles.verifyRow}>
                    {[5, 4, 3, 2, 1].map(s => (
                      <span key={s} style={{ marginRight: 14 }}>
                        <strong>{s}★</strong> {dist.counts[String(s)]}
                      </span>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, color: '#4b5563', marginTop: 6 }}>
                    {dist.star_sum} stars ÷ {dist.total} reviews = <strong>{dist.mean.toFixed(4)}</strong>,
                    which is the {dist.displayed_rating} Airbnb displays. Every number on this page is
                    arithmetic on those counts, not an estimate.
                  </div>
                </div>
              )}
              {!dist.verified && (
                <div style={styles.approxBox}>
                  <strong>Approximate.</strong> We couldn't reconcile this listing's star breakdown
                  with the rating Airbnb displays, so treat these figures as indicative rather than exact.
                </div>
              )}

              {/* Gated detail */}
              {showDetail ? (
                <>
                  {guestFav && (
                    <div style={styles.card}>
                      <h3 style={styles.cardTitle}>Guest Favorite (4.9)</h3>
                      <p style={{ ...styles.seoText, marginBottom: 8 }}>
                        {guestFav.meets
                          ? `You clear the 4.9 target, with room for ${guestFav.buffer_one_stars} more 1-star review${guestFav.buffer_one_stars === 1 ? '' : 's'}.`
                          : guestFav.five_stars_needed === 1
                            ? `You are ${Math.abs(guestFav.margin).toFixed(4)} below the 4.9 target - a single 5-star review would take you over it.`
                            : `You are ${Math.abs(guestFav.margin).toFixed(4)} below the 4.9 target - ${guestFav.five_stars_needed} consecutive 5-star reviews.`}
                        {!guestFav.meets && !superhost.meets && superhost.five_stars_needed > 0 && (
                          <> That is {(guestFav.five_stars_needed / superhost.five_stars_needed).toFixed(1)}× the work of reaching 4.8, because 4.9 leaves half the headroom.</>
                        )}
                      </p>
                      <p style={{ fontSize: 12, color: '#9ca3af', margin: 0 }}>{guestFav.caveat}</p>
                    </div>
                  )}

                  {result.category_drag?.length > 0 && (
                    <div style={styles.card}>
                      <h3 style={styles.cardTitle}>What's pulling you down</h3>
                      {result.category_drag.map(c => (
                        <div key={c.category} style={styles.dragRow}>
                          <span style={{ fontWeight: 600 }}>{c.category}</span>
                          <span style={{ color: '#f59e0b', fontWeight: 700 }}>
                            {c.rating.toFixed(1)} <span style={{ color: '#9ca3af', fontWeight: 500 }}>({c.gap_to_overall.toFixed(2)})</span>
                          </span>
                        </div>
                      ))}
                      <p style={{ ...styles.seoText, fontSize: 13, marginTop: 10 }}>
                        These sit below your overall rating. Airbnb's overall star is given separately
                        by guests, so these don't arithmetically set your mean - they show why guests
                        are holding back the fifth star.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div style={styles.card}>
                  <h3 style={styles.cardTitle}>See the full breakdown</h3>
                  <p style={{ ...styles.seoText, marginBottom: 12 }}>
                    Your Guest Favorite position and the categories pulling your rating down.
                    Enter your email to unlock.
                  </p>
                  <form onSubmit={handleUnlock}>
                    <input
                      style={styles.input}
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      disabled={unlocking}
                    />
                    <label style={styles.consentRow}>
                      <input
                        type="checkbox"
                        checked={marketingConsent}
                        onChange={e => setMarketingConsent(e.target.checked)}
                        style={{ marginTop: 2 }}
                      />
                      <span>Email me occasional tips for Airbnb hosts. You can unsubscribe at any time.</span>
                    </label>
                    <button
                      style={{ ...styles.btn, marginTop: 12, opacity: unlocking || !email.includes('@') ? 0.45 : 1 }}
                      type="submit" disabled={unlocking || !email.includes('@')}
                    >
                      {unlocking ? 'Unlocking...' : 'Unlock the breakdown'}
                    </button>
                  </form>
                </div>
              )}

              {/* The caveat — never bury this */}
              <div style={styles.caveatBox}>
                <strong>One important caveat.</strong> {result.caveat}
              </div>

              {/* Share + CTA */}
              {result.share_url && (
                <div style={styles.card}>
                  <h3 style={styles.cardTitle}>Share this report</h3>
                  <p style={{ ...styles.seoText, marginBottom: 12 }}>
                    Anyone with this link sees the full report - useful for a co-host or your
                    property manager.
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <input style={{ ...styles.input, flex: '1 1 240px' }} readOnly value={result.share_url} />
                    <button style={styles.btnGhost} onClick={handleCopyShare} type="button">
                      {copiedShare ? 'Copied' : 'Copy link'}
                    </button>
                  </div>
                </div>
              )}

              <div style={styles.ctaCard}>
                <h3 style={{ ...styles.cardTitle, fontSize: 17 }}>Know the moment this changes</h3>
                <p style={{ ...styles.seoText, marginBottom: 16 }}>
                  This page is your standing today, from public data. StellarReply tracks it
                  continuously, works from your true trailing-12-month figure once you connect
                  your account, and tells you the moment a new review moves you toward the line.
                </p>
                <button style={styles.btn} onClick={startTrial}>Start free trial</button>
              </div>
            </div>
          )}
        </div>
      </section>

      <footer style={styles.footer}>
        <div style={{ marginBottom: 6 }}>
          <a href="/free-airbnb-listing-audit" style={styles.footerLink}>Listing audit</a>
          {' · '}
          <a href="/free-airbnb-review-response-generator" style={styles.footerLink}>Reply generator</a>
          {' · '}
          <a href="/for-airbnb-hosts" style={styles.footerLink}>For Airbnb hosts</a>
        </div>
        StellarReply - reputation tooling for Airbnb hosts and local businesses.
      </footer>
    </div>
  );
}

const cssText = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&display=swap');
  @keyframes standingFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
  .standing-fade { animation: standingFadeIn 0.5s ease both; }
  @media (prefers-reduced-motion: reduce) { .standing-fade { animation: none; } }
`;

const styles = {
  page: { fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif", minHeight: '100vh', background: '#faf9f6' },
  nav: { borderBottom: '1px solid #e8e5df', background: '#fff' },
  navInner: { maxWidth: 900, margin: '0 auto', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { fontSize: 18, fontWeight: 800, color: '#1a1a1a', cursor: 'pointer' },
  navCta: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  hero: { padding: '60px 20px 20px', textAlign: 'center' },
  heroInner: { maxWidth: 620, margin: '0 auto' },
  badge: { display: 'inline-block', fontSize: 12, fontWeight: 600, color: '#16a34a', background: 'rgba(22,163,74,0.08)', padding: '4px 14px', borderRadius: 20, marginBottom: 16 },
  h1: { fontSize: 36, fontWeight: 800, color: '#1a1a1a', lineHeight: 1.2, margin: '0 0 16px' },
  heroSub: { fontSize: 15, color: '#6b7280', lineHeight: 1.6, margin: '0 0 32px', maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' },
  form: { maxWidth: 520, margin: '0 auto', textAlign: 'left' },
  input: { width: '100%', padding: '14px 18px', borderRadius: 10, border: '1px solid #d4d1cc', fontSize: 15, fontFamily: "'DM Sans', system-ui, sans-serif", outline: 'none', boxSizing: 'border-box', background: '#fff' },
  btn: { width: '100%', padding: '14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #16a34a, #059669)', color: '#fff', fontSize: 16, fontWeight: 800, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  btnGhost: { padding: '10px 24px', borderRadius: 10, border: '1px solid #d4d1cc', background: '#fff', color: '#1a1a1a', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans', system-ui, sans-serif" },
  errorBox: { marginTop: 16, padding: '12px 16px', background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.15)', borderRadius: 8, color: '#dc2626', fontSize: 13, maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' },
  reportCard: { background: '#fff', borderRadius: 12, border: '1px solid #e8e5df', padding: '20px 24px', marginBottom: 16 },
  card: { background: '#fff', borderRadius: 12, border: '1px solid #e8e5df', padding: '20px 24px', marginBottom: 16 },
  cardTitle: { fontSize: 15, fontWeight: 700, margin: '0 0 12px', color: '#1a1a1a' },
  seoText: { fontSize: 14, color: '#6b7280', lineHeight: 1.7, margin: 0 },
  verdict: { fontSize: 14, color: '#1a1a1a', lineHeight: 1.65, marginTop: 8, paddingTop: 14, borderTop: '1px solid #f0ede8' },
  statGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 16 },
  stat: { background: '#fff', borderRadius: 12, border: '1px solid #e8e5df', padding: '14px 16px' },
  statLabel: { fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 },
  statValue: { fontSize: 24, fontWeight: 800, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' },
  statSub: { fontSize: 12, color: '#9ca3af', marginTop: 4, lineHeight: 1.4 },
  verify: { background: 'rgba(22,163,74,0.06)', border: '1px solid rgba(22,163,74,0.18)', borderRadius: 12, padding: '14px 18px', marginBottom: 16 },
  verifyRow: { fontSize: 13, color: '#1a1a1a', fontVariantNumeric: 'tabular-nums' },
  approxBox: { background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.22)', borderRadius: 12, padding: '14px 18px', marginBottom: 16, fontSize: 13, color: '#1a1a1a', lineHeight: 1.6 },
  dragRow: { display: 'flex', justifyContent: 'space-between', fontSize: 14, padding: '7px 0', borderBottom: '1px solid #f5f3ef' },
  consentRow: { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: '#6b7280', lineHeight: 1.5, marginTop: 10 },
  caveatBox: { background: '#fff', border: '1px dashed #d4d1cc', borderRadius: 12, padding: '14px 18px', marginBottom: 16, fontSize: 13, color: '#6b7280', lineHeight: 1.65 },
  ctaCard: { background: '#fff', borderRadius: 12, border: '2px solid #16a34a', padding: '28px', marginTop: 8 },
  footer: { textAlign: 'center', padding: '20px', fontSize: 12, color: '#9ca3af', borderTop: '1px solid #e8e5df' },
  footerLink: { color: '#6b7280', textDecoration: 'none' },
};
